import { HeadlessSuperConverter } from '@/Components/SuperEditor/Tools/HeadlessSuperConverter';
import { AppDataField, ApplicationService, ApplicationEvent, removeFromArray, InternalEventBus, assert, WebAppEvent, PayloadEmitSource, } from '@standardnotes/snjs';
export class DesktopManager extends ApplicationService {
    constructor(application, device, backups) {
        super(application, new InternalEventBus());
        this.device = device;
        this.backups = backups;
        this.updateObservers = [];
        this.dataLoaded = false;
        this.needsInitialTextBackup = false;
        const markdownConverter = new HeadlessSuperConverter();
        backups.setSuperConverter(markdownConverter);
    }
    async handleWatchedDirectoriesChanges(changes) {
        void this.backups.importWatchedDirectoryChanges(changes);
    }
    async handleHomeServerStarted(_serverUrl) { }
    beginTextBackupsTimer() {
        if (this.textBackupsInterval) {
            clearInterval(this.textBackupsInterval);
        }
        this.needsInitialTextBackup = true;
        const hoursInterval = 12;
        const seconds = hoursInterval * 60 * 60;
        const milliseconds = seconds * 1000;
        this.textBackupsInterval = setInterval(this.saveDesktopBackup, milliseconds);
    }
    get webApplication() {
        return this.application;
    }
    deinit() {
        this.updateObservers.length = 0;
        super.deinit();
    }
    async onAppEvent(eventName) {
        super.onAppEvent(eventName).catch(console.error);
        if (eventName === ApplicationEvent.LocalDataLoaded) {
            this.dataLoaded = true;
            if (this.backups.isTextBackupsEnabled()) {
                this.beginTextBackupsTimer();
            }
        }
        else if (eventName === ApplicationEvent.MajorDataChange) {
            void this.saveDesktopBackup();
        }
    }
    async saveDesktopBackup() {
        var _a;
        this.webApplication.notifyWebEvent(WebAppEvent.BeganBackupDownload);
        const data = await this.getBackupFile();
        if (data) {
            await ((_a = this.webApplication.fileBackups) === null || _a === void 0 ? void 0 : _a.saveTextBackupData(data));
            this.webApplication.notifyWebEvent(WebAppEvent.EndedBackupDownload, { success: true });
        }
    }
    async getBackupFile() {
        const encrypted = this.application.hasProtectionSources();
        const result = encrypted
            ? await this.application.createEncryptedBackupFile.execute({ skipAuthorization: true })
            : await this.application.createDecryptedBackupFile.execute();
        if (result.isFailed()) {
            return undefined;
        }
        return JSON.stringify(result.getValue(), null, 2);
    }
    getExtServerHost() {
        assert(this.device.extensionsServerHost);
        return this.device.extensionsServerHost;
    }
    /**
     * Sending a component in its raw state is really slow for the desktop app
     * Keys are not passed into ItemParams, so the result is not encrypted
     */
    convertComponentForTransmission(component) {
        return component.payloadRepresentation().ejected();
    }
    syncComponentsInstallation(components) {
        Promise.all(components.map((component) => {
            return this.convertComponentForTransmission(component);
        }))
            .then((payloads) => {
            this.device.syncComponents(payloads);
        })
            .catch(console.error);
    }
    registerUpdateObserver(callback) {
        const observer = {
            callback: callback,
        };
        this.updateObservers.push(observer);
        return () => {
            removeFromArray(this.updateObservers, observer);
        };
    }
    searchText(text) {
        this.lastSearchedText = text;
        this.device.onSearch(text);
    }
    redoSearch() {
        if (this.lastSearchedText) {
            this.searchText(this.lastSearchedText);
        }
    }
    updateAvailable() {
        this.webApplication.notifyWebEvent(WebAppEvent.NewUpdateAvailable);
    }
    windowGainedFocus() {
        this.webApplication.notifyWebEvent(WebAppEvent.WindowDidFocus);
    }
    windowLostFocus() {
        this.webApplication.notifyWebEvent(WebAppEvent.WindowDidBlur);
        if (this.needsInitialTextBackup) {
            this.needsInitialTextBackup = false;
            void this.saveDesktopBackup();
        }
    }
    consoleLog(message) {
        // eslint-disable-next-line no-console
        console.log(message);
    }
    async onComponentInstallationComplete(componentData) {
        const component = this.application.items.findItem(componentData.uuid);
        if (!component) {
            return;
        }
        const updatedComponent = (await this.application.changeAndSaveItem.execute(component, (m) => {
            const mutator = m;
            mutator.local_url = componentData.content.local_url;
            mutator.package_info = componentData.content.package_info;
            mutator.setAppDataItem(AppDataField.ComponentInstallError, undefined);
        }, undefined, PayloadEmitSource.DesktopComponentSync)).getValue();
        for (const observer of this.updateObservers) {
            observer.callback(updatedComponent);
        }
    }
}
